import styles from './style.module.css';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faEnvelope} from '@fortawesome/free-regular-svg-icons';
import {faMapMarkerAlt, faPhoneAlt} from '@fortawesome/free-solid-svg-icons';
import FormGroup from '@components/Forms/FormGroup';
import TextField from '@components/Forms/TextField';
import Button from '@components/Forms/Button';
import {useForm} from 'react-hook-form';
import {useRequest} from '@hooks/use-request';
import {toast} from 'react-toastify';
import {useTranslation} from 'next-i18next';
import {ContactReasonType, FeedbackReason} from '@constants/feedback';
import {useRouter} from 'next/router';
import {LocaleEnum} from '@enums/locale';
import {RecaptchaWrapper} from '@components/RecaptchaWrapper';

export const FeedbackPageComponent = () => {
  const {t} = useTranslation('feedback');
  const {register, handleSubmit, reset, setValue} = useForm({});
  const router = useRouter();
  const locale = router.locale as LocaleEnum;
  const {sendRequest, loading} = useRequest();
  const onSubmit = async (data: unknown) => {
    await sendRequest<string>({
      url: '/feedback',
      method: 'post',
      body: data,
      onSuccess: () => {
        reset();
        toast.success(t<string>('form.thank_you_message'));
      },
      onError: error => {
        console.log(error);
        toast.error(t<string>('form.error_message'));
      },
    });
  };

  return (
    <div>
      <div className={styles.feedbackBanner}>
        <div>
          <h1>{t<string>('title')}</h1>
        </div>
      </div>
      <div className={`container ${styles.feedbackContainer}`}>
        <div className="row">
          <div className={`col-md-6 col-lg-5 ${styles.feedbackInfoSection}`}>
            <div className={styles.infoContent}>
              <h3>{t<string>('contact_information')}</h3>
              <p>{t<string>('info_one')}</p>
              <p>{t<string>('info_two')}</p>
              <ul className={styles.contactInfoList}>
                <li>
                  <h5>
                    <FontAwesomeIcon icon={faEnvelope} />{' '}
                    {t<string>('chat_to_us')}
                  </h5>
                  <div>{t<string>('emailaddress')}</div>
                </li>
                <li>
                  <h5>
                    <FontAwesomeIcon icon={faMapMarkerAlt} />{' '}
                    {t<string>('address')}
                  </h5>
                  <div>
                    <p>
                      <b>Liune Door / Aulis Lundell Oy</b>
                    </p>
                    <p>Tarrankuja 2 08500 Lohja</p>
                  </div>
                </li>
                <li>
                  <h5>
                    {' '}
                    <FontAwesomeIcon icon={faPhoneAlt} />{' '}
                    {t<string>('phone_number')}
                  </h5>
                  <div>
                    <p>(+358) 020 7341 400</p>
                  </div>
                </li>
              </ul>
            </div>
          </div>
          <div className={`col-md-6 col-lg-7 ${styles.feedbackForm}`}>
            <h2>{t<string>('form.title')}</h2>
            <p>{t<string>('form.instruction')}</p>
            <form onSubmit={handleSubmit(onSubmit)}>
              <RecaptchaWrapper
                register={register}
                setToken={(token: string) => {
                  setValue('recaptchaToken', token);
                }}
                action="feedback_form"
              />
              {/* Honey pot field for basic bots and fallback to recaptcha */}
              <input type="hidden" {...register('x_random_field')} />
              <FormGroup>
                <label htmlFor="">{t<string>('form.feedback_reason')}</label>
                <TextField required {...register('reason')} type={'select'}>
                  <option value="">
                    [{t<string>('form.feedback_reason')}]
                  </option>
                  {FeedbackReason[locale].map((reason, index) => (
                    <option key={index} value={reason.value}>
                      {reason.title}
                    </option>
                  ))}
                </TextField>
              </FormGroup>
              <FormGroup>
                <label htmlFor="">{t<string>('form.contacting_type')}</label>
                <TextField
                  required
                  {...register('contact_type')}
                  type={'select'}
                >
                  <option value="">
                    [{t<string>('form.contacting_type')}]
                  </option>
                  {ContactReasonType[locale].map((type, index) => (
                    <option key={index} value={type.value}>
                      {type.title}
                    </option>
                  ))}
                </TextField>
              </FormGroup>
              <FormGroup>
                <label htmlFor="">{t<string>('form.name')}</label>
                <TextField
                  {...register('name')}
                  placeholder={t<string>('form.name')}
                />
              </FormGroup>
              <FormGroup>
                <label htmlFor="">{t<string>('form.business_id')}</label>
                <TextField
                  {...register('business_id')}
                  required
                  placeholder={t<string>('form.business_id')}
                />
              </FormGroup>
              <FormGroup>
                <label htmlFor="">{t<string>('phone_number')}</label>
                <TextField
                  {...register('phone_number')}
                  placeholder={t<string>('phone_number')}
                />
              </FormGroup>
              <FormGroup>
                <label htmlFor="">{t<string>('form.email')}</label>
                <TextField
                  {...register('email')}
                  required
                  placeholder={t<string>('form.email')}
                />
              </FormGroup>
              <FormGroup>
                <label htmlFor="">{t<string>('form.address')}</label>
                <TextField
                  {...register('address')}
                  placeholder={t<string>('form.address')}
                />
              </FormGroup>

              <FormGroup>
                <label htmlFor="">{t<string>('form.message')}</label>
                <textarea
                  required
                  {...register('message')}
                  className={'form-control'}
                  rows={3}
                  placeholder={t<string>('form.message_placeholder')}
                ></textarea>
              </FormGroup>

              <FormGroup>
                <Button
                  disabled={loading}
                  type={'submit'}
                  classNames={'mt-3'}
                  size={'lg'}
                >
                  {t<string>('form.send_message')}
                </Button>
              </FormGroup>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};
